import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { FormActionButtons, FormField, Label } from '../../../components/Form/FormComponents';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { KpiConfigPeriod, KpiConfigPeriodToName } from '../../../data-models/company-financials.data-model';
import { IField, KpiConfigSection } from '../../../data-models/field2.data-model';
import {
  bulkUpdateKpiDisplayConfig,
  COMPANY_VIEWS_COMPONENT,
  IKpiDisplaySelection,
} from '../../../services/queries/FieldDisplayConfigQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import {
  ACCRUED_DISPLAY_PERIODS,
  END_OF_PERIOD_DISPLAY_PERIODS,
  kpiDisplayComboKey,
  kpiDisplayComboLabel,
  kpiDisplaySections,
  kpiSupportedComboKeys,
} from './fieldManagerModels';

// Every period the column fan-out can synthesize, in display order.
const ALL_DISPLAY_PERIODS = [
  ...ACCRUED_DISPLAY_PERIODS.filter((p) => !END_OF_PERIOD_DISPLAY_PERIODS.includes(p)),
  ...END_OF_PERIOD_DISPLAY_PERIODS,
];

// Same labels as the KPI form's Scenario field (capitalized raw values).
const SECTION_TYPE_LABEL: Record<KpiConfigSection, string> = {
  [KpiConfigSection.Actual]: 'Actual',
  [KpiConfigSection.Budget]: 'Budget',
  [KpiConfigSection.Both]: 'Both',
};

interface BulkKpiDisplayModalProps {
  kpis: IField<unknown>[];
  onClose: () => void;
}

/**
 * Bulk "Set Field Display" for selected KPIs: the chosen scenario × periods
 * REPLACE each KPI's Company Views config; combinations a KPI cannot
 * synthesize are skipped for that KPI by the backend (the warning below makes
 * that visible upfront). Only display config is bulk-editable — no other part
 * of a KPI.
 */
export function BulkKpiDisplayModal({ kpis, onClose }: BulkKpiDisplayModalProps) {
  const { pushErrorToast, pushSuccessToast, pushWarningToast } = useToastMessageState();
  // Starts ON with nothing selected so Apply is disabled on open: a misclick can
  // never silently deregister the selection. Turning the switch OFF is the
  // deliberate deregister action.
  const [enabled, setEnabled] = useState(true);
  const [sectionType, setSectionType] = useState<KpiConfigSection>(KpiConfigSection.Both);
  const [periods, setPeriods] = useState<KpiConfigPeriod[]>([]);
  const [saving, setSaving] = useState(false);

  const combos = useMemo<IKpiDisplaySelection[]>(() => {
    const sections = kpiDisplaySections(sectionType);
    return periods.flatMap((period) => sections.map((section) => ({ period, section })));
  }, [sectionType, periods]);

  // KPIs whose config cannot synthesize every chosen combination.
  const unsupportedKpis = useMemo(() => {
    if (combos.length === 0) {
      return [];
    }
    return kpis.filter((kpi) => {
      const supported = kpiSupportedComboKeys(kpi);
      return combos.some((c) => !supported.has(kpiDisplayComboKey(c.period, c.section)));
    });
  }, [kpis, combos]);

  const onApply = async () => {
    setSaving(true);
    const selections = enabled ? combos : [];
    try {
      const result = await bulkUpdateKpiDisplayConfig(
        kpis.map((k) => k.id),
        { component: COMPANY_VIEWS_COMPONENT, selections }
      );
      const updatedMessage = `Updated the Company Views display of ${result.updated.length} KPI${
        result.updated.length === 1 ? '' : 's'
      }.`;
      if (result.skipped.length > 0) {
        pushWarningToast({
          message: `${updatedMessage} ${result.skipped.length} could no longer be found and ${
            result.skipped.length === 1 ? 'was' : 'were'
          } skipped.`,
        });
      } else {
        pushSuccessToast({ message: updatedMessage });
      }
      window.location.reload();
    } catch (err) {
      pushErrorToast({
        message: getErrorMessage(err, 'Failed to update the Company Views display settings.'),
      });
      setSaving(false);
    }
  };

  const unsupportedNames = unsupportedKpis.map((k) => k.displayName);
  const shownNames = unsupportedNames.slice(0, 5).join(', ');
  const extraCount = unsupportedNames.length - 5;

  return (
    <BasicDialog open title={`Edit Field Display (${kpis.length} KPIs)`} onClose={onClose}>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '34rem', p: '0 2.5rem 1rem' }}
      >
        <Typography variant='body2' color='text.secondary'>
          Replaces the Company Views display settings of the selected KPIs. No other KPI settings are changed.
        </Typography>
        <FormControlLabel
          control={
            <Switch checked={enabled} disabled={saving} onChange={(e) => setEnabled(e.target.checked)} />
          }
          label='Include in Company Views'
        />
        {!enabled && (
          <Typography variant='caption' color='text.secondary'>
            Applying with this off removes the selected KPIs from Company Views.
          </Typography>
        )}
        {enabled && (
          <>
            <FormField>
              <Label>Scenario</Label>
              <Select
                value={sectionType}
                disabled={saving}
                inputProps={{ 'aria-label': 'Scenario' }}
                onChange={(e) => setSectionType(e.target.value as KpiConfigSection)}
              >
                {Object.values(KpiConfigSection).map((value) => (
                  <MenuItem key={value} value={value}>
                    {SECTION_TYPE_LABEL[value]}
                  </MenuItem>
                ))}
              </Select>
            </FormField>
            <FormField>
              <Label>Period</Label>
              <Select
                multiple
                value={periods}
                disabled={saving}
                inputProps={{ 'aria-label': 'Period' }}
                onChange={(e) => setPeriods(e.target.value as KpiConfigPeriod[])}
                renderValue={(value) => value.map((p) => KpiConfigPeriodToName[p]).join(', ')}
              >
                {ALL_DISPLAY_PERIODS.map((period) => (
                  <MenuItem key={period} value={period}>
                    <Checkbox checked={periods.includes(period)} />
                    <ListItemText primary={KpiConfigPeriodToName[period]} />
                  </MenuItem>
                ))}
              </Select>
            </FormField>
            {combos.length > 0 && (
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Resulting columns
                </Typography>
                <Typography variant='body2'>
                  {combos.map((c) => kpiDisplayComboLabel(c.period, c.section)).join(', ')}
                </Typography>
              </Box>
            )}
            {unsupportedKpis.length > 0 && (
              <Typography variant='caption' color='warning.main'>
                {unsupportedKpis.length} of {kpis.length} selected KPI
                {unsupportedKpis.length === 1 ? ' does' : 's do'} not support some of these combinations (
                {shownNames}
                {extraCount > 0 ? ` +${extraCount} more` : ''}) — unsupported ones are skipped for those KPIs.
              </Typography>
            )}
          </>
        )}
        <FormActionButtons>
          <LoadingButton
            color='secondary'
            variant='contained'
            loading={saving}
            disabled={saving || (enabled && combos.length === 0)}
            onClick={onApply}
          >
            Apply
          </LoadingButton>
          <Button color='secondary' variant='outlined' disabled={saving} onClick={onClose}>
            Cancel
          </Button>
        </FormActionButtons>
      </Box>
    </BasicDialog>
  );
}
